import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import adminApiService from '../../services/adminApiService';
import toast from 'react-hot-toast';
import {
  CurrencyDollarIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  XMarkIcon,
  UsersIcon,
  BriefcaseIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline';

interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  userType: 'student' | 'employer';
  tier: 'free' | 'basic' | 'plus' | 'premium' | 'pro' | 'enterprise';
  monthlyPrice: number;
  yearlyPrice: number;
  currency: string;
  isActive: boolean;
  features: any;
  limits: any;
}

const defaultFeatures = {
  student: {
    // Course & Learning Features
    basicCoursesAccess: true,      // Free access to basic courses
    allCoursesAccess: false,       // Premium access to all courses including advanced
    timedAssessments: true,        // Basic timed assessments
    skillAssessmentRetakes: false, // Limited retakes on free tier
    learningPathRecommendations: false,
    interactiveMaterials: false,   // Advanced interactive content
    certificationGeneration: true, // Basic certificates
    
    // Job Application Features  
    basicJobMatching: true,        // Basic job recommendations
    priorityJobMatching: false,    // AI-powered priority matching
    jobApplicationTracking: true,  // Track application status
    jobStatusManagement: true,     // Employment status on profile
    
    // Profile & Analytics
    basicProfile: true,            // Standard profile
    portfolioBuilder: false,       // Advanced portfolio features
    basicAnalytics: true,          // Basic progress tracking
    advancedAnalytics: false,      // Detailed performance insights
    aiCvGeneration: false,         // AI-generated resumes
    customProfileThemes: false,
    
    // Support & Extras
    communitySupport: true,        // Community forums
    prioritySupport: false,        // Direct support access
    mentorshipAccess: false,       // 1-on-1 mentorship
    exclusiveWebinars: false,      // Premium content
    
    // Employer features (not applicable to students)
    jobPostingAccess: false,
    candidateSearchAccess: false,
    backgroundCheckAccess: false,
    socialMediaAnalysisAccess: false,
    customCourseCreation: false,
    candidateRankingAccess: false,
    courseRequirementSetting: false,
    employeeManagement: false,
    bulkMessaging: false,
    detailedCandidateAnalytics: false,
    longitudinalDataAccess: false,
    customBranding: false,
    apiAccess: false,
    dedicatedAccountManager: false,
    whitelabelSolution: false,
    ssoIntegration: false,
    customIntegrations: false
  },
  employer: {
    // Student features (employers can also take courses)
    basicCoursesAccess: true,
    allCoursesAccess: false,
    timedAssessments: true,
    skillAssessmentRetakes: false,
    learningPathRecommendations: false,
    interactiveMaterials: false,
    certificationGeneration: true,
    basicJobMatching: false,       // Not applicable
    priorityJobMatching: false,
    jobApplicationTracking: false,
    jobStatusManagement: false,
    basicProfile: true,
    portfolioBuilder: false,
    basicAnalytics: true,
    advancedAnalytics: false,
    aiCvGeneration: false,
    customProfileThemes: false,
    communitySupport: true,
    prioritySupport: false,
    mentorshipAccess: false,
    exclusiveWebinars: false,
    
    // Core Employer Features
    jobPostingAccess: true,            // Basic job posting
    candidateSearchAccess: true,       // Search candidates
    basicCandidateView: true,          // View candidate profiles
    jobApplicationManagement: true,    // Manage applications
    employeeManagement: true,          // Track hired employees
    
    // Course Integration Features
    courseRequirementSetting: false,   // Set courses as job requirements
    candidateRankingAccess: false,     // AI-powered candidate ranking
    courseCompletionTracking: false,   // Track candidate course progress
    aiJobCourseMatching: false,        // AI course-to-job matching
    
    // Advanced Features
    backgroundCheckAccess: false,      // Background screening
    socialMediaAnalysisAccess: false,  // Social media analysis
    customCourseCreation: false,       // Create custom assessments
    advancedFiltering: false,          // Advanced candidate filtering
    bulkMessaging: false,              // Message multiple candidates
    detailedCandidateAnalytics: false, // Deep candidate insights
    longitudinalDataAccess: false,     // Long-term performance data
    
    // Enterprise Features
    customBranding: false,
    apiAccess: false,
    dedicatedAccountManager: false,
    whitelabelSolution: false,
    ssoIntegration: false,
    customIntegrations: false
  }
};

const defaultLimits = {
  student: {
    // Course & Learning Limits (Free Tier - Reasonable for individual learning)
    basicCoursesPerMonth: 3,         // 3 basic courses per month
    allCoursesPerMonth: 0,           // No premium courses on free
    timedAssessmentsPerMonth: 5,     // 5 assessments per month
    assessmentRetakes: 1,            // 1 retake per assessment
    certificatesPerMonth: 2,         // 2 certificates per month
    
    // Job Application Limits
    jobApplicationsPerMonth: 10,     // 10 job applications per month
    jobAlertsActive: 3,              // 3 active job alerts
    profileViews: 50,                // Profile can be viewed 50 times/month
    
    // Profile & Portfolio
    portfolioProjects: 0,            // No portfolio on free tier
    profileCustomizations: 0,        // No custom themes
    
    // Support & Communication
    supportTicketsPerMonth: 2,       // 2 support tickets per month
    communityPosts: 10,              // 10 community posts per month
    
    // Not applicable to students (set to 0)
    jobPostingsPerMonth: 0,
    candidateViewsPerMonth: 0,
    candidateMessagesPerMonth: 0,
    backgroundChecksPerMonth: 0,
    customCoursesTotal: 0,
    teamMembersTotal: 0,
    dataExportPerMonth: 0,
    apiCallsPerMonth: 0,
    employeesManaged: 0
  },
  employer: {
    // Course & Learning Limits (Employers can also take courses)
    basicCoursesPerMonth: 5,         // 5 courses per month for employer learning
    allCoursesPerMonth: 0,           // No premium courses on free
    timedAssessmentsPerMonth: 3,     // 3 assessments per month
    assessmentRetakes: 1,            // 1 retake per assessment
    certificatesPerMonth: 1,         // 1 certificate per month
    
    // Core Employer Limits (Free Tier - Suitable for small businesses)
    jobPostingsPerMonth: 2,          // 2 job postings per month
    activeJobPostings: 3,            // 3 active jobs at once
    candidateViewsPerMonth: 50,      // View 50 candidate profiles per month
    candidateMessagesPerMonth: 20,   // Message 20 candidates per month
    applicationResponsesPerMonth: 50, // Respond to 50 applications per month
    
    // Employee Management
    employeesManaged: 5,             // Manage up to 5 employees
    employeeStatusUpdates: 20,       // 20 status updates per month
    
    // Course Integration Limits
    courseRequirementsPerJob: 0,     // No course requirements on free tier
    candidateRankingViews: 0,        // No candidate ranking on free tier
    courseProgressTracking: 0,       // No course tracking on free tier
    
    // Advanced Feature Limits (Free Tier)
    backgroundChecksPerMonth: 0,     // No background checks on free
    socialMediaAnalyses: 0,          // No social media analysis on free
    customCoursesTotal: 0,           // No custom course creation on free
    bulkMessagesPerMonth: 0,         // No bulk messaging on free
    advancedSearchesPerMonth: 10,    // 10 advanced searches per month
    
    // Team & Enterprise
    teamMembersTotal: 1,             // Solo employer account
    dataExportPerMonth: 0,           // No data export on free
    apiCallsPerMonth: 0,             // No API access on free
    supportTicketsPerMonth: 2,       // 2 support tickets per month
    
    // Not applicable to employers
    jobApplicationsPerMonth: 0,
    jobAlertsActive: 0,
    profileViews: 0,
    portfolioProjects: 0,
    profileCustomizations: 0,
    communityPosts: 0
  }
};

const SubscriptionPlans: React.FC = () => {
  const { adminUser, hasPermission } = useAdmin();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    userType: 'student' as 'student' | 'employer',
    tier: 'basic' as 'free' | 'basic' | 'plus' | 'premium' | 'pro' | 'enterprise',
    monthlyPrice: 0,
    yearlyPrice: 0,
    currency: 'USD',
    isActive: true,
    features: defaultFeatures.student,
    limits: defaultLimits.student
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  useEffect(() => {
    // Update features and limits when userType changes
    setFormData(prev => ({
      ...prev,
      features: defaultFeatures[formData.userType],
      limits: defaultLimits[formData.userType]
    }));
  }, [formData.userType]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await adminApiService.getSubscriptionPlans();
      
      if (response.success && response.data) {
        setPlans(response.data.plans || []);
      }
    } catch (error: any) {
      console.error('Failed to fetch subscription plans:', error);
      toast.error('Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDefaults = async () => {
    try {
      const response = await adminApiService.createDefaultSubscriptionPlans();
      if (response.success) {
        toast.success('Default subscription plans created successfully');
        fetchPlans();
      } else {
        throw new Error(response.message || 'Failed to create default plans');
      }
    } catch (error: any) {
      console.error('Create default plans error:', error);
      toast.error('Failed to create default plans: ' + (error.message || 'Unknown error'));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (selectedPlan) {
        // Update existing plan
        const response = await adminApiService.updateSubscriptionPlan(selectedPlan.id, formData);
        if (response.success) {
          toast.success('Subscription plan updated successfully');
          setShowEditModal(false);
          setSelectedPlan(null);
          fetchPlans();
        } else {
          throw new Error(response.message || 'Failed to update plan');
        }
      } else {
        // Create new plan
        const response = await adminApiService.createSubscriptionPlan(formData);
        if (response.success) {
          toast.success('Subscription plan created successfully');
          setShowCreateModal(false);
          fetchPlans();
        } else {
          throw new Error(response.message || 'Failed to create plan');
        }
      }
      
      resetForm();
    } catch (error: any) {
      console.error('Submit plan error:', error);
      toast.error('Failed to save plan: ' + (error.message || 'Unknown error'));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      userType: 'student',
      tier: 'basic',
      monthlyPrice: 0,
      yearlyPrice: 0,
      currency: 'USD',
      isActive: true,
      features: defaultFeatures.student,
      limits: defaultLimits.student
    });
  };

  const handleEdit = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
    setFormData({
      name: plan.name,
      description: plan.description,
      userType: plan.userType,
      tier: plan.tier,
      monthlyPrice: plan.monthlyPrice,
      yearlyPrice: plan.yearlyPrice,
      currency: plan.currency,
      isActive: plan.isActive,
      features: plan.features || defaultFeatures[plan.userType],
      limits: plan.limits || defaultLimits[plan.userType]
    });
    setShowEditModal(true);
  };

  const handleFeatureToggle = (featureName: string) => {
    setFormData(prev => ({
      ...prev,
      features: {
        ...prev.features,
        [featureName]: !(prev.features as any)[featureName]
      }
    }));
  };

  const handleLimitChange = (limitName: string, value: string) => {
    const numValue = value === '' ? 0 : parseInt(value);
    setFormData(prev => ({
      ...prev,
      limits: {
        ...prev.limits,
        [limitName]: numValue
      }
    }));
  };

  const getTierBadge = (tier: string) => {
    const tierConfig = {
      free: { color: 'bg-gray-100 text-gray-800', label: 'Free' },
      basic: { color: 'bg-blue-100 text-blue-800', label: 'Basic' },
      plus: { color: 'bg-indigo-100 text-indigo-800', label: 'Plus' },
      premium: { color: 'bg-purple-100 text-purple-800', label: 'Premium' },
      pro: { color: 'bg-pink-100 text-pink-800', label: 'Pro' },
      enterprise: { color: 'bg-red-100 text-red-800', label: 'Enterprise' }
    };
    
    const config = tierConfig[tier as keyof typeof tierConfig] || tierConfig.basic;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getUserTypeIcon = (userType: string) => {
    return userType === 'student' ? (
      <AcademicCapIcon className="h-5 w-5 text-blue-500" />
    ) : (
      <BriefcaseIcon className="h-5 w-5 text-green-500" />
    );
  };

  if (!hasPermission('system', 'read')) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to access subscription management.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Subscription Plans</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage subscription plans and pricing for students and employers
          </p>
          {/* Debug: Show permission status */}
          <div className="mt-2 text-xs text-gray-500">
            System Write Permission: {hasPermission('system', 'write') ? '✅ Granted' : '❌ Missing'} 
            {!hasPermission('system', 'write') && (
              <a 
                href="/admin/dashboard" 
                className="text-red-600 ml-2 hover:text-red-800 underline"
              >
                → Go to Dashboard and click "Update Permissions"
              </a>
            )}
          </div>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <div className="flex items-center space-x-3">
            <button
              onClick={handleCreateDefaults}
              className="inline-flex items-center justify-center rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500"
            >
              Create Defaults
            </button>
            <button
              onClick={() => {
                resetForm();
                setShowCreateModal(true);
              }}
              className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
              New Plan
            </button>
          </div>
        </div>
      </div>

      {/* Plans Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className="relative rounded-lg border border-gray-300 bg-white px-6 py-5 shadow-sm hover:border-gray-400"
            >
              {/* Plan Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  {getUserTypeIcon(plan.userType)}
                  <h3 className="text-lg font-medium text-gray-900">{plan.name}</h3>
                </div>
                <div className="flex items-center space-x-2">
                  {getTierBadge(plan.tier)}
                  {plan.isActive ? (
                    <CheckCircleIcon className="h-5 w-5 text-green-500" />
                  ) : (
                    <XCircleIcon className="h-5 w-5 text-red-500" />
                  )}
                </div>
              </div>

              {/* Description */}
              <p className="text-sm text-gray-600 mb-4">{plan.description}</p>

              {/* Pricing */}
              <div className="mb-4">
                <div className="flex items-baseline space-x-2">
                  <span className="text-2xl font-bold text-gray-900">
                    ${plan.monthlyPrice}
                  </span>
                  <span className="text-sm text-gray-500">/month</span>
                </div>
                {plan.yearlyPrice > 0 && (
                  <div className="text-sm text-gray-600">
                    ${plan.yearlyPrice}/year (save ${(plan.monthlyPrice * 12 - plan.yearlyPrice).toFixed(2)})
                  </div>
                )}
              </div>

              {/* User Type */}
              <div className="mb-4">
                <span className="text-sm font-medium text-gray-700 capitalize">
                  {plan.userType} Plan
                </span>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => handleEdit(plan)}
                  className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                >
                  <PencilSquareIcon className="h-4 w-4 mr-1" />
                  Edit
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
            {/* Header with close button */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                {selectedPlan ? 'Edit Subscription Plan' : 'Create New Subscription Plan'}
              </h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(false);
                  setSelectedPlan(null);
                }}
                className="text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Scrollable content */}
            <div className="overflow-y-auto max-h-[calc(90vh-140px)] p-6">
              
              <form id="subscription-plan-form" onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  {/* Basic Info */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">User Type</label>
                    <select
                      value={formData.userType}
                      onChange={(e) => setFormData({ ...formData, userType: e.target.value as 'student' | 'employer' })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    >
                      <option value="student">Student</option>
                      <option value="employer">Employer</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Tier</label>
                    <select
                      value={formData.tier}
                      onChange={(e) => setFormData({ ...formData, tier: e.target.value as any })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    >
                      <option value="free">Free</option>
                      <option value="basic">Basic</option>
                      <option value="plus">Plus</option>
                      <option value="premium">Premium</option>
                      <option value="pro">Pro</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Monthly Price ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.monthlyPrice}
                      onChange={(e) => setFormData({ ...formData, monthlyPrice: parseFloat(e.target.value) || 0 })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Yearly Price ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.yearlyPrice}
                      onChange={(e) => setFormData({ ...formData, yearlyPrice: parseFloat(e.target.value) || 0 })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                        className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700">Active</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>

                {/* Features */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">Features</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.keys(formData.features).map((featureName) => (
                      <label key={featureName} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={(formData.features as any)[featureName]}
                          onChange={() => handleFeatureToggle(featureName)}
                          className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2 text-sm text-gray-700 capitalize">
                          {featureName.replace(/([A-Z])/g, ' $1').trim()}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Limits */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">Usage Limits (-1 for unlimited)</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.keys(formData.limits).map((limitName) => (
                      <div key={limitName}>
                        <label className="block text-sm font-medium text-gray-700 capitalize">
                          {limitName.replace(/([A-Z])/g, ' $1').trim()}
                        </label>
                        <input
                          type="number"
                          value={(formData.limits as any)[limitName]}
                          onChange={(e) => handleLimitChange(limitName, e.target.value)}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                      </div>
                    ))}
                  </div>
                </div>

              </form>
            </div>

            {/* Fixed Footer with Actions */}
            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
              <button
                type="button"
                onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(false);
                  setSelectedPlan(null);
                  resetForm();
                }}
                className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="subscription-plan-form"
                className="bg-indigo-600 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-indigo-700"
              >
                {selectedPlan ? 'Update Plan' : 'Create Plan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPlans;